'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard,
  CreditCard,
  PiggyBank,
  Receipt,
  BarChart3,
  Plus,
  ChevronRight
} from 'lucide-react';

interface SubNavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: string;
  description?: string;
}

interface FinanceSubNavigationProps {
  className?: string;
}

export function FinanceSubNavigation({ className = '' }: FinanceSubNavigationProps) {
  const pathname = usePathname();

  const navItems: SubNavItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      href: '/finance',
      icon: LayoutDashboard,
      description: 'Dashboard and summary'
    },
    {
      id: 'transactions',
      label: 'Transactions',
      href: '/finance/transactions',
      icon: CreditCard,
      description: 'Manage transactions'
    },
    {
      id: 'budgets',
      label: 'Budgets',
      href: '/finance/budgets',
      icon: PiggyBank,
      description: 'Budget planning'
    },
    {
      id: 'bills',
      label: 'Bills',
      href: '/finance/bills',
      icon: Receipt,
      description: 'Bill reminders'
    },
    {
      id: 'reports',
      label: 'Reports',
      href: '/finance/reports',
      icon: BarChart3,
      description: 'Financial reports'
    }
  ];

  const isActive = (href: string) => {
    if (href === '/finance') {
      return pathname === '/finance';
    }
    return pathname.startsWith(href);
  };

  const getCurrentPageTitle = () => {
    const activeItem = navItems.find(item => isActive(item.href));
    return activeItem?.label || 'Finance';
  };

  const getBreadcrumbs = () => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs = [
      { label: 'Finance', href: '/finance' }
    ];

    if (segments.length > 1) {
      const activeItem = navItems.find(item => isActive(item.href));
      if (activeItem && activeItem.href !== '/finance') {
        breadcrumbs.push({ label: activeItem.label, href: activeItem.href });
      }

      // Add specific page breadcrumbs
      if (segments.includes('add')) {
        breadcrumbs.push({ label: 'Add', href: pathname });
      }
      if (segments.includes('edit')) {
        breadcrumbs.push({ label: 'Edit', href: pathname });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center space-x-1 text-sm text-muted-foreground">
        {breadcrumbs.map((crumb, index) => (
          <div key={crumb.href} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 mx-1" />}
            <Link 
              href={crumb.href}
              className={`hover:text-foreground transition-colors ${
                index === breadcrumbs.length - 1 ? 'text-foreground font-medium' : ''
              }`}
            >
              {crumb.label}
            </Link>
          </div>
        ))}
      </nav>

      {/* Sub Navigation Tabs */}
      <Card className="p-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide">
            {navItems.map((item) => (
              <Link key={item.id} href={item.href}>
                <Button
                  variant={isActive(item.href) ? "default" : "ghost"}
                  className={`flex items-center gap-2 whitespace-nowrap text-xs sm:text-sm ${
                    isActive(item.href) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                  size="sm"
                >
                  <item.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline sm:inline">{item.label}</span>
                  <span className="xs:hidden sm:hidden">{item.label.slice(0, 3)}</span>
                  {item.badge && (
                    <Badge variant="secondary" className="text-xs ml-1">
                      {item.badge}
                    </Badge>
                  )}
                </Button>
              </Link>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2 ml-4">
            {pathname.startsWith('/finance/transactions') && (
              <Link href="/finance/transactions/add">
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Transaction
                </Button>
              </Link>
            )}
            {pathname.startsWith('/finance/budgets') && (
              <Link href="/finance/budgets/add">
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Budget
                </Button>
              </Link>
            )}
            {pathname.startsWith('/finance/bills') && (
              <Link href="/finance/bills/add">
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Bill
                </Button>
              </Link>
            )}
          </div>
        </div>
      </Card>

      {/* Page Title and Description */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{getCurrentPageTitle()}</h1>
          {navItems.find(item => isActive(item.href))?.description && (
            <p className="text-muted-foreground">
              {navItems.find(item => isActive(item.href))?.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}